// ============================================================================
// Profile Cache Service
// ============================================================================
// Centralized caching for user profiles including avatar URLs.
// Provides O(1) lookups and automatic cache invalidation.
// ============================================================================

import { supabase } from 'app/auth/auth.js';
import { CacheStore } from 'app/state/cache-store.js';

// The columns every read path here selects. One constant so the three queries
// (all / one / many) can never drift apart and hand callers a profile whose
// shape depends on which entry point happened to warm the cache.
const PROFILE_COLUMNS = 'user_id, full_name, short_name, avatar_url, hourly_rate, has_training_contract, employment_start_date, timesheet_start_date, timesheet_required, display_title, target_weekly_hours, role, email, can_approve_billing';

class ProfileCacheService {
    constructor() {
        // Cache profiles for 10 minutes (avatars update infrequently)
        this.cache = new CacheStore(200, 10 * 60 * 1000);
        this.allProfilesLoaded = false;
        this.loadPromise = null;
        this.lastLoadTime = 0;
        // Track avatar versions to detect changes
        this.avatarVersions = new Map();
    }

    /**
     * Load all profiles into cache
     * @param {boolean} forceRefresh - Force a refresh even if cache is valid
     * @returns {Promise<Array>} All profiles
     */
    async loadAllProfiles(forceRefresh = false) {
        const now = Date.now();
        const cacheAge = now - this.lastLoadTime;
        const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

        // Return cached data if still valid
        if (!forceRefresh && this.allProfilesLoaded && cacheAge < CACHE_TTL) {
            return this.getAllFromCache();
        }

        // Deduplicate concurrent requests
        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = this._fetchAllProfiles();

        try {
            const profiles = await this.loadPromise;
            return profiles;
        } finally {
            this.loadPromise = null;
        }
    }

    /**
     * Internal method to fetch all profiles
     */
    async _fetchAllProfiles() {
        const { data, error } = await supabase
            .from('profiles')
            .select(PROFILE_COLUMNS)
            .order('full_name');

        if (error) {
            console.error('[ProfileCacheService] Error loading profiles:', error);
            return [];
        }

        // Update cache for each profile
        if (data) {
            for (const profile of data) {
                this.cache.set(`profile:${profile.user_id}`, profile, null); // No expiry for individual profiles
                // Track avatar version
                if (profile.avatar_url) {
                    this.avatarVersions.set(profile.user_id, profile.avatar_url);
                }
            }
            this.allProfilesLoaded = true;
            this.lastLoadTime = Date.now();
        }

        return data || [];
    }

    /**
     * Get all profiles from cache, alphabetically by name. The underlying
     * Map iterates in INSERTION order (individually pre-cached profiles sit
     * at the head), so an explicit sort keeps warm-cache reads consistent
     * with the DB's .order('full_name') on the cold path.
     */
    getAllFromCache() {
        const profiles = [];
        for (const [key, value] of this.cache.cache) {
            if (key.startsWith('profile:') && value.value) {
                profiles.push(value.value);
            }
        }
        return profiles.sort((a, b) =>
            (a.full_name || a.short_name || '').localeCompare(b.full_name || b.short_name || ''));
    }

    /**
     * Get a single profile by user ID
     * @param {string} userId
     * @returns {Promise<Object|null>}
     */
    async getProfile(userId) {
        // Check cache first
        const cached = this.cache.get(`profile:${userId}`);
        if (cached) {
            return cached;
        }

        // Fetch from database
        const { data, error } = await supabase
            .from('profiles')
            .select(PROFILE_COLUMNS)
            .eq('user_id', userId)
            .single();

        if (error) {
            console.error('[ProfileCacheService] Error loading profile:', error);
            return null;
        }

        if (data) {
            this.cache.set(`profile:${data.user_id}`, data);
            if (data.avatar_url) {
                this.avatarVersions.set(data.user_id, data.avatar_url);
            }
        }

        return data;
    }

    /**
     * Get multiple profiles by user IDs
     * @param {string[]} userIds
     * @returns {Promise<Map<string, Object>>}
     */
    async getProfiles(userIds) {
        const result = new Map();
        const uncachedIds = [];

        // Check cache for each ID
        for (const userId of userIds) {
            const cached = this.cache.get(`profile:${userId}`);
            if (cached) {
                result.set(userId, cached);
            } else {
                uncachedIds.push(userId);
            }
        }

        // Fetch uncached profiles
        if (uncachedIds.length > 0) {
            const { data, error } = await supabase
                .from('profiles')
                .select(PROFILE_COLUMNS)
                .in('user_id', uncachedIds);

            if (!error && data) {
                for (const profile of data) {
                    this.cache.set(`profile:${profile.user_id}`, profile);
                    result.set(profile.user_id, profile);
                    if (profile.avatar_url) {
                        this.avatarVersions.set(profile.user_id, profile.avatar_url);
                    }
                }
            }
        }

        return result;
    }

    /**
     * Update a profile in the cache (call after profile updates)
     * @param {Object} profile
     */
    updateProfile(profile) {
        if (!profile?.user_id) return;

        this.cache.set(`profile:${profile.user_id}`, profile);
        if (profile.avatar_url) {
            this.avatarVersions.set(profile.user_id, profile.avatar_url);
        }
    }

    /**
     * Invalidate a specific profile (forces refresh on next access)
     * @param {string} userId
     */
    invalidateProfile(userId) {
        this.cache.delete(`profile:${userId}`);
    }

    /**
     * Check if an avatar URL has changed
     * @param {string} userId
     * @param {string} newAvatarUrl
     * @returns {boolean}
     */
    hasAvatarChanged(userId, newAvatarUrl) {
        const oldUrl = this.avatarVersions.get(userId);
        return oldUrl !== newAvatarUrl;
    }

    /**
     * Clear all cached data
     */
    clear() {
        this.cache.clear();
        this.avatarVersions.clear();
        this.allProfilesLoaded = false;
        this.lastLoadTime = 0;
    }

    /**
     * Get avatar URL for a user (with cache-busting if needed)
     * @param {string} userId
     * @returns {Promise<string|null>}
     */
    async getAvatarUrl(userId) {
        const profile = await this.getProfile(userId);
        return profile?.avatar_url || null;
    }

    /**
     * Get every profile flagged `can_approve_billing`. Sorted by full_name.
     * Hits the same cache as loadAllProfiles() so callers share freshness.
     * @returns {Promise<Array>}
     */
    async getApprovers() {
        const all = await this.loadAllProfiles();
        return all
            .filter(p => p.can_approve_billing)
            .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    }
}

// Export singleton
export const profileCacheService = new ProfileCacheService();
